import moment from 'moment'
import {
  ContractsValueObjectsAdditionalCompensationRecurrenceEnum,
  IncentivesCompensationPlanPeriodFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'
import i18n from 'modules/core/lib/i18n'
import { stringToDate } from 'modules/core/lib/dates'

const QUARTER_START_MONTHS = [0, 3, 6, 9]
const HALF_START_MONTHS = [0, 6]

export const PERIOD_STATUS = {
  ACTIVE: 'active',
  CLOSED: 'closed',
  FUTURE: 'future',
} as const

export type PeriodStatus = (typeof PERIOD_STATUS)[keyof typeof PERIOD_STATUS]
export const getPeriodStatus = (period: IncentivesCompensationPlanPeriodFragment): PeriodStatus => {
  if (isFuturePeriod(period)) return PERIOD_STATUS.FUTURE
  if (isActivePeriod(period)) return PERIOD_STATUS.ACTIVE
  return PERIOD_STATUS.CLOSED // if not future and not active, it's closed
}

const isFuturePeriod = (period: IncentivesCompensationPlanPeriodFragment): boolean => {
  const today = moment().startOf('day')
  const startsOn = stringToDate(period.startsOn, 'backend')
  return startsOn.isAfter(today)
}

const isClosedPeriod = (period: IncentivesCompensationPlanPeriodFragment): boolean => {
  const today = moment().startOf('day')
  const endsOn = stringToDate(period.endsOn, 'backend')
  return endsOn.isBefore(today)
}

const isActivePeriod = (period: IncentivesCompensationPlanPeriodFragment): boolean => {
  const today = moment().startOf('day')
  return today.isBetween(
    stringToDate(period.startsOn, 'backend'),
    stringToDate(period.endsOn, 'backend'),
    undefined,
    '[]'
  )
}

export const isExecutedPeriod = (period: IncentivesCompensationPlanPeriodFragment): boolean => {
  const lastExecution = period.lastExecution
  return Boolean(lastExecution?.generationStatus && lastExecution.generationStatus !== 'failed')
}

export const getCurrentPeriod = (
  periods: IncentivesCompensationPlanPeriodFragment[]
): IncentivesCompensationPlanPeriodFragment | undefined => {
  if (periods.length === 0) return undefined

  const today = moment().startOf('day')

  const activePeriod = periods.find((period) => isActivePeriod(period))

  if (activePeriod) return activePeriod

  const sorted = sortBy(periods, (p) => p.startsOn)

  // Today is before all periods → return the first upcoming one
  const firstUpcoming = sorted.find((p) => stringToDate(p.startsOn, 'backend').isAfter(today))
  if (firstUpcoming) return firstUpcoming

  // Today is after all periods → return the most recent one
  return sortBy(periods, (p) => p.endsOn).at(-1)
}

export const getFirstNotExecutedPeriod = (
  periods: IncentivesCompensationPlanPeriodFragment[]
): IncentivesCompensationPlanPeriodFragment | undefined => {
  return sortBy(periods, (p) => p.startsOn).find((period) => !isExecutedPeriod(period))
}

type PeriodLabelOptions = {
  excludeYear?: boolean
}

export const periodLabel = (
  recurrence: ContractsValueObjectsAdditionalCompensationRecurrenceEnum,
  period: IncentivesCompensationPlanPeriodFragment,
  options?: PeriodLabelOptions
): string => {
  const startDate = stringToDate(period.startsOn, 'backend')
  const endDate = stringToDate(period.endsOn, 'backend')
  const startMonth = startDate.month()
  const sameYear = startDate.year() === endDate.year()
  const shouldExcludeYear = options?.excludeYear === true && sameYear

  switch (recurrence) {
    case 'monthly':
      return shouldExcludeYear
        ? startDate.format('MMMM')
        : `${startDate.format('MMMM')} ${startDate.year()}`

    case 'every_3_months': {
      if (QUARTER_START_MONTHS.includes(startMonth)) {
        const quarterLabel = i18n.t('incentives.periods.types.quarter', {
          quarter: startDate.quarter(),
        })
        return shouldExcludeYear ? quarterLabel : `${quarterLabel} ${startDate.year()}`
      }
      return formatDateRange(startDate, endDate, shouldExcludeYear)
    }

    case 'every_6_months': {
      if (HALF_START_MONTHS.includes(startMonth)) {
        const half = startMonth === 0 ? 1 : 2
        const halfLabel = i18n.t('incentives.periods.types.half', { half })
        return shouldExcludeYear ? halfLabel : `${halfLabel} ${startDate.year()}`
      }
      return formatDateRange(startDate, endDate, shouldExcludeYear)
    }

    case 'every_12_months':
      return String(startDate.year())

    default:
      return formatDateRange(startDate, endDate, shouldExcludeYear)
  }
}

const formatDateRange = (
  startDate: moment.Moment,
  endDate: moment.Moment,
  excludeYear: boolean = false
): string => {
  const startMonth = startDate.format('MMM')
  const endMonth = endDate.format('MMM')
  const startYear = startDate.year()
  const endYear = endDate.year()

  if (excludeYear && startYear === endYear) {
    return `${startMonth} - ${endMonth}`
  }

  return `${startMonth} ${startYear} - ${endMonth} ${endYear}`
}
